import React, { useState, useEffect } from 'react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { Clock, Shield, Phone, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/9e8b8adb-4702-43d4-a386-2eaf3df07374/images/b24ec72530f91b4f8c6cffeb3888d8a62510ccd6594508020dcbad069f0b3db6.png";

const trustPoints = [
  { icon: Clock, text: 'Response within 1 hour' },
  { icon: Shield, text: 'Fully vetted chauffeurs' },
  { icon: CheckCircle2, text: 'No obligation quote' },
  { icon: Phone, text: 'Direct line: +1 (437) 996-3801' },
];

export const ChauffeurBook = () => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [bookingId, setBookingId] = useState('');
  const [formData, setFormData] = useState({
    fullName: '', email: '', mobile: '', pickupDate: '',
    pickupLocation: '', dropoffLocation: '', passengers: '',
    vehiclePreference: '', occasion: '', notes: '',
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('fade-in'); }),
      { threshold: 0.05, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.fade-element').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/chauffeur-bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (res.ok) {
        setBookingId(data.booking_id);
        setSubmitted(true);
        toast({ title: `Booking ${data.booking_id} Received`, description: data.message });
      } else {
        toast({ title: "Error", description: "Something went wrong. Please try again or call us directly.", variant: "destructive" });
      }
    } catch {
      toast({ title: "Error", description: "Unable to submit. Please call us directly.", variant: "destructive" });
    }
    setSubmitting(false);
  };

  const inputCls = "bg-[rgba(22,22,22,0.8)] border-[rgba(194,168,120,0.12)] text-[#F7F3EC] placeholder:text-[rgba(200,200,200,0.25)] focus:border-[rgba(194,168,120,0.4)] focus:bg-[rgba(28,28,28,0.85)] font-light text-sm h-[52px] transition-all duration-300";
  const labelSt = { display: 'block', fontSize: '10px', color: 'rgba(194,168,120,0.6)', letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 400, marginBottom: '10px' };
  const selectSt = {
    width: '100%', height: '52px', padding: '0 16px',
    backgroundColor: 'rgba(22,22,22,0.8)', border: '1px solid rgba(194,168,120,0.12)',
    borderRadius: '0', color: '#F7F3EC', fontSize: '14px', fontWeight: 300,
    appearance: 'none', WebkitAppearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23C2A878' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m2 5 6 6 6-6'/%3e%3c/svg%3e")`,
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 14px center', backgroundSize: '13px',
    outline: 'none', transition: 'all 0.3s ease',
  };

  const SectionHeader = ({ num, title }) => (
    <div className="flex items-center" style={{ gap: '16px', marginBottom: '32px' }}>
      <span style={{ fontSize: '12px', color: 'rgba(194,168,120,0.45)', letterSpacing: '2px', fontWeight: 400 }}>{num}</span>
      <div style={{ width: '24px', height: '1px', backgroundColor: 'rgba(194,168,120,0.2)' }} />
      <span style={{ fontSize: '11px', color: 'rgba(220,220,220,0.6)', letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 400 }}>{title}</span>
    </div>
  );

  return (
    <div data-testid="chauffeur-book-page" style={{ backgroundColor: '#0A0A0A' }}>

      {/* ── HERO ── */}
      <section data-testid="book-hero" className="relative overflow-hidden flex items-center justify-center" style={{ minHeight: '75vh' }}>
        <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url(${HERO_IMG})`,
          filter: 'brightness(0.42) saturate(0.85) contrast(1.08)',
          scale: '1.05',
          animation: 'heroZoom 25s ease-in-out infinite alternate',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, rgba(0,0,0,0.68) 0%, rgba(0,0,0,0.2) 35%, rgba(0,0,0,0.15) 55%, rgba(0,0,0,0.75) 100%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 40%, rgba(194,168,120,0.05) 0%, transparent 55%)',
        }} />
        <div className="grain-overlay absolute inset-0" style={{ opacity: 0.04 }} />

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto" style={{ paddingTop: '160px', paddingBottom: '140px' }}>
          <div className="fade-element" style={{ animationDelay: '0.2s' }}>
            <div className="gold-divider mx-auto" style={{ marginBottom: '28px', opacity: 0.7 }} />
            <p style={{ fontSize: '11px', color: 'rgba(194,168,120,0.5)', letterSpacing: '5px', textTransform: 'uppercase', fontWeight: 300, marginBottom: '32px' }}>
              Toronto Royale Chauffeur
            </p>
          </div>
          <div className="fade-element" style={{ animationDelay: '0.4s' }}>
            <h1 data-testid="book-hero-heading" className="font-serif" style={{
              fontSize: 'clamp(38px, 5.5vw, 64px)', fontWeight: 500, color: '#FAFAFA',
              marginBottom: '28px', lineHeight: 1.08, textShadow: '0 2px 40px rgba(0,0,0,0.5)',
            }}>
              Request Your Chauffeur
            </h1>
          </div>
          <div className="fade-element" style={{ animationDelay: '0.6s' }}>
            <p style={{ fontSize: '17px', color: 'rgba(220,220,220,0.6)', fontWeight: 300, lineHeight: 1.8, maxWidth: '480px', margin: '0 auto' }}>
              Submit your details and we'll confirm availability and send your quote.
            </p>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0" style={{ height: '180px', background: 'linear-gradient(to top, #0A0A0A, transparent)' }} />
      </section>

      {/* ── FORM SECTION ── */}
      <section className="relative overflow-hidden" style={{ backgroundColor: '#0A0A0A', padding: '4rem 1.5rem 10rem' }}>
        {/* Ambient background */}
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 40% 20%, rgba(194,168,120,0.025) 0%, transparent 50%)',
        }} />
        <div className="absolute top-0 left-0 right-0" style={{
          height: '400px',
          backgroundImage: `url(${HERO_IMG})`,
          backgroundSize: 'cover', backgroundPosition: 'center',
          filter: 'brightness(0.12) saturate(0.45)',
          maskImage: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, transparent 100%)',
        }} />

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12" style={{ gap: '56px' }}>

            {/* ── MAIN FORM ── */}
            <div className="lg:col-span-8">
              {submitted ? (
                /* ── SUCCESS STATE ── */
                <div className="fade-element" style={{
                  padding: '80px 56px', textAlign: 'center',
                  backgroundColor: 'rgba(14,14,14,0.7)',
                  backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
                  border: '1px solid rgba(194,168,120,0.1)',
                  boxShadow: '0 24px 80px rgba(0,0,0,0.4)',
                }}>
                  <div style={{
                    width: '64px', height: '64px', margin: '0 auto 32px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: 'rgba(194,168,120,0.06)', border: '1px solid rgba(194,168,120,0.15)',
                    borderRadius: '50%',
                  }}>
                    <CheckCircle2 size={28} style={{ color: '#C2A878', opacity: 0.8 }} />
                  </div>
                  <h2 className="font-serif" style={{ fontSize: '32px', color: '#F5F5F5', marginBottom: '16px' }}>
                    Booking #{bookingId} Received
                  </h2>
                  <p style={{ fontSize: '15px', color: 'rgba(204,204,204,0.55)', fontWeight: 300, lineHeight: 1.8, maxWidth: '420px', margin: '0 auto 36px' }}>
                    Thank you for choosing Toronto Royale Chauffeur. We'll confirm availability and send your quote shortly.
                  </p>
                  <button
                    data-testid="book-another-btn"
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({ fullName: '', email: '', mobile: '', pickupDate: '', pickupLocation: '', dropoffLocation: '', passengers: '', vehiclePreference: '', occasion: '', notes: '' });
                    }}
                    style={{
                      backgroundColor: 'transparent', color: '#C2A878', padding: '13px 28px',
                      fontSize: '12px', fontWeight: 400, letterSpacing: '1px', textTransform: 'uppercase',
                      border: '1px solid rgba(194,168,120,0.25)', cursor: 'pointer', transition: 'all 400ms ease',
                    }}
                    onMouseEnter={(e) => { e.target.style.backgroundColor = 'rgba(194,168,120,0.06)'; e.target.style.borderColor = 'rgba(194,168,120,0.45)'; }}
                    onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; e.target.style.borderColor = 'rgba(194,168,120,0.25)'; }}
                  >
                    Submit Another Request
                  </button>
                </div>
              ) : (
                /* ── FORM CARD ── */
                <div className="fade-element" style={{
                  backgroundColor: 'rgba(14,14,14,0.7)',
                  backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
                  border: '1px solid rgba(194,168,120,0.08)',
                  boxShadow: '0 24px 80px rgba(0,0,0,0.4), inset 0 1px 0 rgba(194,168,120,0.04)',
                  padding: 'clamp(36px, 5vw, 64px)',
                }}>
                  {/* Gold top accent */}
                  <div style={{
                    position: 'absolute', top: 0, left: '15%', right: '15%', height: '1px',
                    background: 'linear-gradient(90deg, transparent, rgba(194,168,120,0.15), transparent)',
                  }} />

                  <form onSubmit={handleSubmit} data-testid="chauffeur-booking-form">

                    {/* SECTION 1: Your Details */}
                    <SectionHeader num="01" title="Your Details" />
                    <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: '20px', marginBottom: '20px' }}>
                      <div>
                        <label style={labelSt}>Full Name</label>
                        <Input type="text" name="fullName" value={formData.fullName} onChange={handleChange} required data-testid="input-fullname" className={inputCls} placeholder="Your full name" />
                      </div>
                      <div>
                        <label style={labelSt}>Email</label>
                        <Input type="email" name="email" value={formData.email} onChange={handleChange} required data-testid="input-email" className={inputCls} placeholder="your@email.com" />
                      </div>
                    </div>
                    <div style={{ marginBottom: '48px' }}>
                      <label style={labelSt}>Mobile Number</label>
                      <Input type="tel" name="mobile" value={formData.mobile} onChange={handleChange} required data-testid="input-mobile" className={inputCls} placeholder="+1 (416) 555-0123" />
                    </div>

                    {/* Divider */}
                    <div style={{ height: '1px', background: 'linear-gradient(90deg, transparent, rgba(194,168,120,0.08), transparent)', marginBottom: '48px' }} />

                    {/* SECTION 2: Journey Details */}
                    <SectionHeader num="02" title="Journey Details" />
                    <div style={{ marginBottom: '20px' }}>
                      <label style={labelSt}>Pickup Date & Time</label>
                      <Input type="datetime-local" name="pickupDate" value={formData.pickupDate} onChange={handleChange} required data-testid="input-pickup-date" className={inputCls} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: '20px', marginBottom: '20px' }}>
                      <div>
                        <label style={labelSt}>Pickup Location</label>
                        <Input type="text" name="pickupLocation" value={formData.pickupLocation} onChange={handleChange} required data-testid="input-pickup-location" className={inputCls} placeholder="Your pickup address" />
                      </div>
                      <div>
                        <label style={labelSt}>Drop-off Location</label>
                        <Input type="text" name="dropoffLocation" value={formData.dropoffLocation} onChange={handleChange} required data-testid="input-dropoff-location" className={inputCls} placeholder="Pearson Airport, Terminal 1" />
                      </div>
                    </div>
                    <div style={{ marginBottom: '48px' }}>
                      <label style={labelSt}>Number of Passengers</label>
                      <select name="passengers" value={formData.passengers} onChange={handleChange} required data-testid="input-passengers" style={selectSt}
                        onFocus={(e) => { e.target.style.borderColor = 'rgba(194,168,120,0.4)'; e.target.style.backgroundColor = 'rgba(28,28,28,0.85)'; }}
                        onBlur={(e) => { e.target.style.borderColor = 'rgba(194,168,120,0.12)'; e.target.style.backgroundColor = 'rgba(22,22,22,0.8)'; }}
                      >
                        <option value="" disabled style={{ color: '#666' }}>Select</option>
                        {[1, 2, 3, 4, 5, 6].map(n => (
                          <option key={n} value={n} style={{ backgroundColor: '#161616', color: '#F7F3EC' }}>{n}</option>
                        ))}
                      </select>
                    </div>

                    {/* Divider */}
                    <div style={{ height: '1px', background: 'linear-gradient(90deg, transparent, rgba(194,168,120,0.08), transparent)', marginBottom: '48px' }} />

                    {/* SECTION 3: Preferences */}
                    <SectionHeader num="03" title="Preferences" />
                    <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: '20px', marginBottom: '20px' }}>
                      <div>
                        <label style={labelSt}>Vehicle Preference</label>
                        <select name="vehiclePreference" value={formData.vehiclePreference} onChange={handleChange} data-testid="input-vehicle" style={selectSt}
                          onFocus={(e) => { e.target.style.borderColor = 'rgba(194,168,120,0.4)'; e.target.style.backgroundColor = 'rgba(28,28,28,0.85)'; }}
                          onBlur={(e) => { e.target.style.borderColor = 'rgba(194,168,120,0.12)'; e.target.style.backgroundColor = 'rgba(22,22,22,0.8)'; }}
                        >
                          <option value="" style={{ color: '#666' }}>No preference</option>
                          <option value="Cadillac Escalade ESV" style={{ backgroundColor: '#161616', color: '#F7F3EC' }}>Cadillac Escalade ESV</option>
                          <option value="Mercedes-Benz S-Class" style={{ backgroundColor: '#161616', color: '#F7F3EC' }}>Mercedes-Benz S-Class</option>
                          <option value="Chevrolet Suburban Premier" style={{ backgroundColor: '#161616', color: '#F7F3EC' }}>Chevrolet Suburban Premier</option>
                        </select>
                      </div>
                      <div>
                        <label style={labelSt}>Occasion</label>
                        <select name="occasion" value={formData.occasion} onChange={handleChange} data-testid="input-occasion" style={selectSt}
                          onFocus={(e) => { e.target.style.borderColor = 'rgba(194,168,120,0.4)'; e.target.style.backgroundColor = 'rgba(28,28,28,0.85)'; }}
                          onBlur={(e) => { e.target.style.borderColor = 'rgba(194,168,120,0.12)'; e.target.style.backgroundColor = 'rgba(22,22,22,0.8)'; }}
                        >
                          <option value="" style={{ color: '#666' }}>Select occasion</option>
                          <option value="Airport Transfer" style={{ backgroundColor: '#161616', color: '#F7F3EC' }}>Airport Transfer</option>
                          <option value="Corporate" style={{ backgroundColor: '#161616', color: '#F7F3EC' }}>Corporate</option>
                          <option value="Event" style={{ backgroundColor: '#161616', color: '#F7F3EC' }}>Event</option>
                          <option value="Cross-Border" style={{ backgroundColor: '#161616', color: '#F7F3EC' }}>Cross-Border</option>
                          <option value="Other" style={{ backgroundColor: '#161616', color: '#F7F3EC' }}>Other</option>
                        </select>
                      </div>
                    </div>
                    <div style={{ marginBottom: '48px' }}>
                      <label style={labelSt}>Notes / Special Requests</label>
                      <Textarea
                        name="notes" value={formData.notes} onChange={handleChange} rows={4}
                        data-testid="input-notes"
                        className="bg-[rgba(22,22,22,0.8)] border-[rgba(194,168,120,0.12)] text-[#F7F3EC] placeholder:text-[rgba(200,200,200,0.25)] focus:border-[rgba(194,168,120,0.4)] focus:bg-[rgba(28,28,28,0.85)] font-light text-sm transition-all duration-300"
                        placeholder="Child seat, multiple stops, specific timing..."
                      />
                    </div>

                    {/* SUBMIT */}
                    <button
                      type="submit" disabled={submitting}
                      data-testid="submit-chauffeur-booking-btn"
                      style={{
                        width: '100%', padding: '16px', fontSize: '12px', fontWeight: 500,
                        letterSpacing: '1.2px', textTransform: 'uppercase',
                        backgroundColor: submitting ? '#9e8a66' : '#C2A878', color: '#0A0A0A',
                        border: 'none', cursor: submitting ? 'wait' : 'pointer',
                        transition: 'all 400ms ease',
                        boxShadow: '0 4px 24px rgba(194,168,120,0.12)',
                      }}
                      onMouseEnter={(e) => { if (!submitting) { e.target.style.backgroundColor = '#D4BA8A'; e.target.style.boxShadow = '0 6px 32px rgba(194,168,120,0.22)'; } }}
                      onMouseLeave={(e) => { if (!submitting) { e.target.style.backgroundColor = '#C2A878'; e.target.style.boxShadow = '0 4px 24px rgba(194,168,120,0.12)'; } }}
                    >
                      {submitting ? 'Submitting Your Request...' : 'Submit Chauffeur Request'}
                    </button>
                  </form>
                </div>
              )}
            </div>

            {/* ── SIDE PANEL ── */}
            <div className="lg:col-span-4">
              <div className="lg:sticky" style={{ top: '140px' }}>

                {/* Trust Card */}
                <div className="fade-element" style={{
                  padding: '40px 32px',
                  backgroundColor: 'rgba(14,14,14,0.5)',
                  backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                  border: '1px solid rgba(194,168,120,0.06)',
                  marginBottom: '32px',
                }}>
                  <p style={{ fontSize: '10px', color: 'rgba(194,168,120,0.45)', letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 400, marginBottom: '28px' }}>
                    What to Expect
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    {trustPoints.map((point, i) => (
                      <div key={i} className="flex items-start" style={{ gap: '14px' }}>
                        <div style={{
                          width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          backgroundColor: 'rgba(194,168,120,0.04)', border: '1px solid rgba(194,168,120,0.08)',
                          flexShrink: 0,
                        }}>
                          <point.icon size={15} strokeWidth={1.5} style={{ color: '#C2A878', opacity: 0.6 }} />
                        </div>
                        <span style={{ fontSize: '13px', color: 'rgba(204,204,204,0.55)', fontWeight: 300, lineHeight: 1.6, paddingTop: '8px' }}>
                          {point.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Quote */}
                <div className="fade-element" style={{
                  padding: '36px 32px',
                  backgroundColor: 'rgba(14,14,14,0.3)',
                  borderLeft: '2px solid rgba(194,168,120,0.12)',
                }}>
                  <p className="font-serif" style={{ fontSize: '16px', fontStyle: 'italic', color: 'rgba(220,220,220,0.4)', lineHeight: 1.8, marginBottom: '20px' }}>
                    "Your comfort and confidence are our priority. Every detail is coordinated with care."
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '24px', height: '1px', backgroundColor: 'rgba(194,168,120,0.2)' }} />
                    <span style={{ fontSize: '11px', color: 'rgba(194,168,120,0.4)', letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 300 }}>
                      Toronto Royale
                    </span>
                  </div>
                </div>

                {/* Prefer to Call */}
                <div className="fade-element" style={{ marginTop: '32px', padding: '28px 32px', backgroundColor: 'rgba(194,168,120,0.03)', border: '1px solid rgba(194,168,120,0.06)' }}>
                  <p style={{ fontSize: '11px', color: 'rgba(194,168,120,0.4)', letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 400, marginBottom: '12px' }}>
                    Prefer to speak directly?
                  </p>
                  <a href="tel:+14379963801" style={{ fontSize: '18px', color: '#C2A878', textDecoration: 'none', fontWeight: 400, letterSpacing: '0.5px', transition: 'opacity 300ms ease' }}
                    onMouseEnter={(e) => { e.target.style.opacity = '0.7'; }}
                    onMouseLeave={(e) => { e.target.style.opacity = '1'; }}
                  >
                    +1 (437) 996-3801
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes heroZoom {
          0% { transform: scale(1.05); }
          100% { transform: scale(1.12); }
        }
      `}</style>
    </div>
  );
};
